/**
 * WO#1 — Slot Economy domain foundation: TERRITORY MODEL.
 *
 * Vocabulary (WO#1 §24 — kept deliberately distinct):
 *   - PROPERTY UNIT  : a single owned parcel/property rectangle (canonical
 *                      ownership geometry). Never mutated here.
 *   - TERRITORY      : a connected component of same-wallet property units
 *                      joined by EDGE adjacency.
 *   - TERRITORY AREA : the physical area of the geometric UNION of a
 *                      territory's units (overlaps counted once; holes
 *                      owned by others excluded — no bounding-box leakage).
 *   - GP CAPACITY    : gridPowerCapacity(area), tier-independent.
 *
 * FROZEN RULES:
 *   - Adjacency is EDGE-only: two units connect iff they share a border
 *     segment of length > 0. Corner-only / single-point / gaps do NOT
 *     connect. No diagonal connectivity (§11).
 *   - Aggregation is an application/domain layer: it MUST NOT merge NFTs,
 *     rewrite canonical geometry, or erase underlying tier metadata (§12).
 *   - Capacity is derived from TOTAL continuous area, never the sum of
 *     component parcels' historical GP (anti-fragmentation, §14).
 *   - Disconnected same-wallet territories keep SEPARATE GP pools (§15).
 *
 * Pure functions only. No React, no DOM, no canonical-math dependency.
 */
import { gridPowerCapacity } from "./grid-power";

/** A single owned parcel rectangle. Underlying tier metadata is preserved. */
export interface PropertyUnit {
  /** Stable identifier of the underlying ownership unit. */
  id: string;
  /** Owner wallet address (lower/upper-case treated as caller-normalized). */
  owner: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Underlying canonical tier/zone classification (preserved, never flattened). */
  tier?: string;
}

/** A continuous same-wallet territory (connected component of units). */
export interface Territory {
  owner: string;
  units: PropertyUnit[];
  /** Physical area of the geometric union of `units`. */
  area: number;
  /** GP capacity derived from `area` (tier-independent). */
  capacity: number;
}

/**
 * Canonical EVM owner identity. Hexadecimal letter casing (checksum vs
 * lowercase) MUST NOT split ownership: `0xAbCd…` and `0xabcd…` are the
 * SAME owner. This is the SINGLE authoritative normalization used by all
 * ownership grouping/comparison in the domain — no scattered `.toLowerCase()`.
 */
export function normalizeOwnerIdentity(owner: string): string {
  return owner.trim().toLowerCase();
}

/**
 * Authoritative geometry validation. A property unit MUST have finite
 * coordinates and STRICTLY positive width/height. Invalid geometry is
 * rejected explicitly (throws) rather than silently reinterpreted — the
 * foundation must never fabricate a malformed territory. Reused by every
 * territory geometry function below.
 */
export function assertValidPropertyGeometry(u: PropertyUnit): void {
  if (
    !Number.isFinite(u.x) ||
    !Number.isFinite(u.y) ||
    !Number.isFinite(u.w) ||
    !Number.isFinite(u.h) ||
    u.w <= 0 ||
    u.h <= 0
  ) {
    throw new RangeError(
      `Invalid property geometry for unit "${u.id}": x=${u.x} y=${u.y} w=${u.w} h=${u.h}`,
    );
  }
}

/**
 * EDGE adjacency: true iff `a` and `b` share a border segment of length
 * > 0. Corner-only and gaps return false. No diagonal connectivity.
 */
export function areEdgeAdjacent(a: PropertyUnit, b: PropertyUnit): boolean {
  assertValidPropertyGeometry(a);
  assertValidPropertyGeometry(b);
  const aL = a.x;
  const aR = a.x + a.w;
  const aT = a.y;
  const aB = a.y + a.h;
  const bL = b.x;
  const bR = b.x + b.w;
  const bT = b.y;
  const bB = b.y + b.h;

  // Touch along a common vertical line (a's right == b's left, or vice-versa).
  if (aR === bL || bR === aL) {
    const verticalOverlap = Math.min(aB, bB) - Math.max(aT, bT);
    if (verticalOverlap > 0) return true;
  }
  // Touch along a common horizontal line (a's bottom == b's top, or vice-versa).
  if (aB === bT || bB === aT) {
    const horizontalOverlap = Math.min(aR, bR) - Math.max(aL, bL);
    if (horizontalOverlap > 0) return true;
  }
  return false;
}

/**
 * Exact area of the geometric UNION of `units` via coordinate compression.
 * Overlapping regions are counted once; regions NOT covered by any unit
 * (e.g. a hole owned by another wallet) are excluded. This guarantees the
 * territory area is never inflated to its bounding box.
 */
export function unionArea(units: readonly PropertyUnit[]): number {
  if (units.length === 0) return 0;
  for (const u of units) assertValidPropertyGeometry(u);
  const xsSet = new Set<number>();
  const ysSet = new Set<number>();
  for (const u of units) {
    xsSet.add(u.x);
    xsSet.add(u.x + u.w);
    ysSet.add(u.y);
    ysSet.add(u.y + u.h);
  }
  const xs = [...xsSet].sort((p, q) => p - q);
  const ys = [...ysSet].sort((p, q) => p - q);

  let area = 0;
  for (let i = 0; i < xs.length - 1; i++) {
    const x0 = xs[i];
    const x1 = xs[i + 1];
    const dx = x1 - x0;
    if (dx <= 0) continue;
    const cx = (x0 + x1) / 2;
    for (let j = 0; j < ys.length - 1; j++) {
      const y0 = ys[j];
      const y1 = ys[j + 1];
      const dy = y1 - y0;
      if (dy <= 0) continue;
      const cy = (y0 + y1) / 2;
      let covered = false;
      for (const u of units) {
        if (cx > u.x && cx < u.x + u.w && cy > u.y && cy < u.y + u.h) {
          covered = true;
          break;
        }
      }
      if (covered) area += dx * dy;
    }
  }
  return area;
}

/**
 * Group units into territories: FIRST by owner wallet, THEN by edge-adjacency
 * connected components. Each component's area is the union area and its
 * capacity is derived once from that total area (anti-fragmentation).
 */
export function buildTerritories(units: readonly PropertyUnit[]): Territory[] {
  for (const u of units) assertValidPropertyGeometry(u);
  const byOwner = new Map<string, PropertyUnit[]>();
  for (const u of units) {
    // Group by CANONICAL owner identity so hex casing never splits a wallet.
    const key = normalizeOwnerIdentity(u.owner);
    const arr = byOwner.get(key);
    if (arr) arr.push(u);
    else byOwner.set(key, [u]);
  }

  const territories: Territory[] = [];
  for (const [owner, list] of byOwner) {
    const visited = new Set<number>();
    for (let i = 0; i < list.length; i++) {
      if (visited.has(i)) continue;
      const component: PropertyUnit[] = [];
      const stack: number[] = [i];
      visited.add(i);
      while (stack.length > 0) {
        const k = stack.pop() as number;
        component.push(list[k]);
        for (let m = 0; m < list.length; m++) {
          if (visited.has(m)) continue;
          if (areEdgeAdjacent(list[k], list[m])) {
            visited.add(m);
            stack.push(m);
          }
        }
      }
      const area = unionArea(component);
      territories.push({
        owner,
        units: component,
        area,
        capacity: gridPowerCapacity(area),
      });
    }
  }
  return territories;
}

/** Distinct underlying tiers present in a territory (metadata is preserved). */
export function territoryTiers(t: Territory): string[] {
  const s = new Set<string>();
  for (const u of t.units) if (u.tier !== undefined) s.add(u.tier);
  return [...s];
}

/** All territories belonging to a given owner (separate GP pools each). */
export function territoriesForOwner(
  territories: readonly Territory[],
  owner: string,
): Territory[] {
  const key = normalizeOwnerIdentity(owner);
  return territories.filter((t) => t.owner === key);
}
